"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/*
 * Copyright Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const react_1 = __importDefault(require("react"));
const react_i18next_1 = require("react-i18next");
const react_fontawesome_1 = require("@fortawesome/react-fontawesome");
const faBan_1 = require("@fortawesome/free-solid-svg-icons/faBan");
const faCheck_1 = require("@fortawesome/free-solid-svg-icons/faCheck");
const faCheckDouble_1 = require("@fortawesome/free-solid-svg-icons/faCheckDouble");
const faTriangleExclamation_1 = require("@fortawesome/free-solid-svg-icons/faTriangleExclamation");
const faRotate_1 = require("@fortawesome/free-solid-svg-icons/faRotate");
const reviewDecisionStatusHelper_1 = require("../../../services/admin/reviewDecisionStatusHelper");
const reviewDecisionButtonsState_1 = require("../../../services/admin/reviewDecisionButtonsState");
const selectableKeyboard_1 = require("../../../services/admin/selectableKeyboard");
/**
 * Approve/reject controls for one reviewable survey object in the admin column.
 * Active decision shows a circular background at 0.3 opacity. Conflicts show a warning icon.
 * @param props.objectType - Survey object type key
 * @param props.objectUuid - Survey object uuid
 * @param props.status - Aggregated review status for this object
 * @param props.canForceApprove - Whether the user may force-approve (confirm permission)
 * @param props.onApprove - Called when the reviewer approves
 * @param props.onReject - Called when the reviewer rejects
 * @param props.onClearReview - Called when the reviewer toggles off their current decision
 * @param props.onForceApprove - Called when an admin force-approves
 * @param props.onClearForceApprove - Called when an admin toggles off their force-approve
 * @param props.onRequestReReview - Called to ask the other reviewers to look again
 * @param props.approvalBlocked - Whether a rejected parent hides approving
 */
const ObjectReviewButtons = ({ objectType, objectUuid, status, canForceApprove, onApprove, onReject, onClearReview, onForceApprove, onClearForceApprove, onRequestReReview, approvalBlocked = false }) => {
    const { t } = (0, react_i18next_1.useTranslation)('admin');
    if (!objectUuid || !(0, reviewDecisionStatusHelper_1.isReviewableObjectType)(objectType)) {
        return null;
    }
    const stopClickPropagation = (event) => {
        event.stopPropagation();
        event.preventDefault();
    };
    const { rejectPressed, approvePressed, forceApprovePressed, showConflictWarning, canClearDecision, showForceApprove, showRequestReReview, reReviewPressed, askedToReReview } = (0, reviewDecisionButtonsState_1.getReviewDecisionButtonsState)(status);
    const renderReviewToggleButton = ({ labelKey, icon, colorClass, isPressed, activeClassSuffix, onActivate, onClear, canClearWhenPressed = true }) => {
        if (!onActivate) {
            return null;
        }
        return (react_1.default.createElement("button", { type: "button", className: `admin__survey-object-box__review-button ${colorClass}${isPressed ? ` ${activeClassSuffix}` : ''}`, title: t(labelKey), "aria-label": t(labelKey), "aria-pressed": isPressed, onClick: (event) => {
                stopClickPropagation(event);
                if (isPressed && canClearWhenPressed && onClear) {
                    onClear();
                    return;
                }
                onActivate();
            } },
            react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: icon })));
    };
    return (react_1.default.createElement("span", { className: "admin__survey-object-box__review-controls", onClick: stopClickPropagation, onKeyDown: selectableKeyboard_1.stopActivationKeyPropagation },
        askedToReReview && (react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: faRotate_1.faRotate, className: "_yellow admin__survey-object-box__review-warning", title: t('interviewMember.reReviewRequested'), "aria-label": t('interviewMember.reReviewRequested'), role: "img" })),
        showConflictWarning && (react_1.default.createElement(react_fontawesome_1.FontAwesomeIcon, { icon: faTriangleExclamation_1.faTriangleExclamation, className: "_yellow admin__survey-object-box__review-warning", title: t('interviewMember.reviewConflict'), "aria-label": t('interviewMember.reviewConflict'), role: "img" })),
        renderReviewToggleButton({
            labelKey: 'interviewMember.rejectObject',
            icon: faBan_1.faBan,
            colorClass: '_red',
            isPressed: rejectPressed,
            activeClassSuffix: 'admin__survey-object-box__review-button--active-reject',
            onActivate: onReject,
            onClear: onClearReview,
            canClearWhenPressed: canClearDecision
        }),
        (!approvalBlocked || (approvePressed && canClearDecision)) &&
            renderReviewToggleButton({
                labelKey: approvalBlocked ? 'interviewMember.withdrawApprove' : 'interviewMember.approveObject',
                icon: faCheck_1.faCheck,
                colorClass: '_green',
                isPressed: approvePressed,
                activeClassSuffix: 'admin__survey-object-box__review-button--active-approve',
                onActivate: approvalBlocked ? onClearReview : onApprove,
                onClear: onClearReview,
                canClearWhenPressed: canClearDecision
            }),
        canForceApprove &&
            onForceApprove &&
            showForceApprove &&
            renderReviewToggleButton({
                labelKey: 'interviewMember.forceApproveObject',
                icon: faCheckDouble_1.faCheckDouble,
                colorClass: '_green',
                isPressed: forceApprovePressed,
                activeClassSuffix: 'admin__survey-object-box__review-button--active-force',
                onActivate: onForceApprove,
                onClear: onClearForceApprove
            }),
        showRequestReReview &&
            onRequestReReview &&
            renderReviewToggleButton({
                labelKey: 'interviewMember.requestReReview',
                icon: faRotate_1.faRotate,
                colorClass: '',
                isPressed: reReviewPressed,
                activeClassSuffix: 'admin__survey-object-box__review-button--active-rereview',
                onActivate: onRequestReReview,
                canClearWhenPressed: false
            })));
};
exports.default = ObjectReviewButtons;
//# sourceMappingURL=ObjectReviewButtons.js.map